<template>
  <section class="app-table-wrap invoices-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">#</th>
            <th>
              <button
                type="button"
                class="invoices-table-sort-btn"
                :title="labels.sortTitles.date"
                @click="emitSort('date')"
              >
                <span>{{ labels.columns.date }}</span>
                <i class="fa" :class="sortIconClass('date')" aria-hidden="true"></i>
              </button>
            </th>
            <th>
              <button
                type="button"
                class="invoices-table-sort-btn"
                :title="labels.sortTitles.customer"
                @click="emitSort('customer')"
              >
                <span>{{ labels.columns.customer }}</span>
                <i class="fa" :class="sortIconClass('customer')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ labels.columns.folio }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="invoices-table-sort-btn invoices-table-sort-btn--right"
                :title="labels.sortTitles.subtotal"
                @click="emitSort('subtotal')"
              >
                <span>{{ labels.columns.subtotal }}</span>
                <i class="fa" :class="sortIconClass('subtotal')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">{{ labels.columns.transferedTaxes }}</th>
            <th class="app-text-right">{{ labels.columns.withheldTaxes }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="invoices-table-sort-btn invoices-table-sort-btn--right"
                :title="labels.sortTitles.total"
                @click="emitSort('total')"
              >
                <span>{{ labels.columns.total }}</span>
                <i class="fa" :class="sortIconClass('total')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">
              <button
                type="button"
                class="invoices-table-sort-btn invoices-table-sort-btn--right"
                :title="labels.sortTitles.balance"
                @click="emitSort('balance')"
              >
                <span>{{ labels.columns.balance }}</span>
                <i class="fa" :class="sortIconClass('balance')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="invoices-table-col-status">{{ labels.columns.status }}</th>
            <th>{{ labels.columns.payment }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="12" class="app-table-empty">{{ loadingText }}</td>
          </tr>

          <tr v-else-if="normalizedRows.length === 0">
            <td :colspan="12" class="app-table-empty">{{ emptyText }}</td>
          </tr>

          <tr v-else v-for="(row, index) in normalizedRows" :key="row.id || index">
            <td class="app-muted">{{ rowNumber(index) }}</td>
            <td>{{ formatDate(row.date) }}</td>
            <td>
              <div class="expenses-cell-stack">
                <a
                  v-if="row?.customer_id"
                  :href="resolveCustomerHref(row)"
                  class="expenses-table-link expenses-cell-main"
                  :title="labels.links.customerDetail"
                  @click="navigateToCustomerDetail(row, $event)"
                >
                  {{ resolveCustomerName(row) }}
                </a>
                <span v-else class="expenses-cell-main">{{ resolveCustomerName(row) }}</span>

                <a
                  v-if="row?.sale_id"
                  :href="resolveSaleHref(row)"
                  class="expenses-table-link expenses-cell-sub"
                  :title="labels.links.saleDetail"
                  @click="navigateToSaleDetail(row, $event)"
                >
                  {{ resolveSaleDisplayName(row) }}
                </a>
                <span v-else class="expenses-cell-sub">{{ resolveSaleDisplayName(row) }}</span>
              </div>
            </td>
            <td>
              <div class="invoices-cell-stack">
                <a
                  v-if="row?.id"
                  :href="resolveInvoiceHref(row)"
                  class="expenses-table-link"
                  :title="labels.actions.viewInvoice"
                  @click="navigateToInvoiceDetail(row, $event)"
                >
                  <strong>{{ resolveSerialFolio(row) }}</strong>
                </a>
                <strong v-else>{{ resolveSerialFolio(row) }}</strong>
                <small>{{ labels.labels.uuid }}: {{ row.uuid || labels.labels.noUuid }}</small>
              </div>
            </td>
            <td class="app-text-right app-strong">
              <strong class="amount-cell-subtotal">{{ formatAmountWithCurrency(row.subtotal, row.currency) }}</strong>
            </td>
            <td class="app-text-right"><span class="amount-cell-tax-line">{{ formatTransferredTaxes(row) }}</span></td>
            <td class="app-text-right"><span class="amount-cell-tax-line">{{ formatWithheldTaxes(row) }}</span></td>
            <td class="app-text-right app-strong">
              <span class="amount-cell-total">{{ formatAmountWithCurrency(row.total, row.currency) }}</span>
              <small v-if="shouldShowExchangeRate(row)" class="amount-cell-exchange-rate">
                {{ resolveExchangeRateLabel(row) }}
              </small>
            </td>
            <td class="app-text-right app-strong">{{ formatAmountWithCurrency(row.balance, row.currency) }}</td>
            <td class="invoices-table-col-status">
              <span class="app-status-pill expenses-status-pill" :class="resolveStatusClass(row)">
                {{ resolveStatusLabel(row) }}
              </span>
            </td>
            <td>
              <button
                v-if="showPaymentAction(row)"
                type="button"
                class="expenses-action-link"
                @click="$emit('pay-invoice', row)"
              >
                {{ labels.actions.pay }}
              </button>
              <a
                v-else-if="hasPaymentReceiptRoute(row)"
                :href="resolvePaymentHref(row)"
                class="expenses-table-link"
                @click="navigateToPaymentDetail(row, $event)"
              >
                {{ resolvePaymentReference(row) }}
              </a>
              <span v-else>{{ resolvePaymentReference(row) }}</span>
            </td>
            <td class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--view"
                  :title="labels.actions.viewInvoice"
                  @click="$emit('view-invoice', row)"
                >
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </button>

                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--download invoices-table-icon-btn--download"
                  :title="labels.actions.download"
                  @click="$emit('download-files', row)"
                >
                  <i class="fa fa-download" aria-hidden="true"></i>
                </button>

                <div class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="labels.actions.more"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="normalizedPagination.total_pages > 1" class="app-table-footer">
      <AppPagination
        :current-page="normalizedPagination.current_page"
        :total-pages="normalizedPagination.total_pages"
        @page-change="requestPage"
      />
    </div>

    <teleport to="body">
      <div
        v-if="openRow"
        class="app-table-actions-menu app-table-floating-menu"
        :style="floatingMenuStyle"
      >
        <button type="button" @click="emitAction('download-xml', openRow)">{{ labels.actions.downloadXml }}</button>
        <button type="button" @click="emitAction('download-pdf', openRow)">{{ labels.actions.downloadPdf }}</button>
        <button type="button" @click="emitAction('upload-receipt', openRow)">{{ labels.actions.uploadReceipt }}</button>
        <button type="button" @click="emitAction('clone-invoice', openRow)">{{ labels.actions.cloneInvoice }}</button>
      </div>
    </teleport>
  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import { formatDateShort } from '@/utils/datePresentation';
import { formatAmountWithCurrency, formatExchangeRate } from '@/utils/numberFormat';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';
import {
  resolveSortIconClass,
  resolveSortTogglePayload,
} from '@/composables/shared/useSorting';

export default {
  name: 'InvoicesTable',
  components: {
    AppPagination,
  },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Object,
      required: true,
    },
    loadingText: {
      type: String,
      required: true,
    },
    emptyText: {
      type: String,
      required: true,
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      }),
    },
    sortField: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
  },
  emits: [
    'view-invoice',
    'pay-invoice',
    'download-files',
    'download-xml',
    'download-pdf',
    'upload-receipt',
    'clone-invoice',
    'page-change',
    'sort-change',
  ],
  data() {
    return {
      openActionsMenuId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    normalizedPagination() {
      const raw = this.pagination || {};

      return {
        current_page: Number(raw.current_page || 1),
        per_page: Number(raw.per_page || 20),
        total_pages: Number(raw.total_pages || 1),
        total_items: Number(raw.total_items || 0),
      };
    },
    openRow() {
      if (!this.openActionsMenuId) {
        return null;
      }

      return this.normalizedRows.find((row) => Number(row?.id || 0) === Number(this.openActionsMenuId)) || null;
    },
    floatingMenuStyle() {
      return {
        position: 'fixed',
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
        zIndex: 1300,
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick, true);
    window.addEventListener('resize', this.closeActionsMenu);
    window.addEventListener('scroll', this.closeActionsMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
    window.removeEventListener('resize', this.closeActionsMenu);
    window.removeEventListener('scroll', this.closeActionsMenu, true);
  },
  methods: {
    formatAmountWithCurrency,
    formatExchangeRate,
    formatTransferredTaxes(row) {
      const amount = Number(row?.transfered_taxes ?? row?.transferred_taxes ?? 0) || 0;
      if (amount <= 0) {
        return '';
      }

      return `+ ${formatAmountWithCurrency(amount, row?.currency)}`;
    },
    formatWithheldTaxes(row) {
      const amount = Number(row?.withheld_taxes ?? 0) || 0;
      if (amount <= 0) {
        return '';
      }

      return `- ${formatAmountWithCurrency(amount, row?.currency)}`;
    },
    rowNumber(index) {
      const currentPage = Number(this.normalizedPagination.current_page || 1);
      const perPage = Number(this.normalizedPagination.per_page || 20);

      return ((currentPage - 1) * perPage) + Number(index) + 1;
    },
    formatDate(value) {
      return formatDateShort(value) || this.resolveEmptyValue();
    },
    sortIconClass(field) {
      return resolveSortIconClass(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );
    },
    emitSort(field) {
      const nextSort = resolveSortTogglePayload(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );

      if (!nextSort) {
        return;
      }

      this.$emit('sort-change', nextSort);
    },
    requestPage(page) {
      const nextPage = Number(page || 1);
      if (!Number.isFinite(nextPage)) {
        return;
      }

      if (nextPage < 1 || nextPage > this.normalizedPagination.total_pages) {
        return;
      }

      if (nextPage === this.normalizedPagination.current_page) {
        return;
      }

      this.$emit('page-change', nextPage);
    },
    resolveCustomerName(row) {
      const customerName = String(row?.customer_name || row?.company_name || row?.cliente || '').trim();
      if (customerName !== '') {
        return customerName;
      }

      if (this.resolveSaleDisplayName(row) !== '') {
        return String(this.labels?.labels?.multiclient || this.resolveEmptyValue());
      }

      return this.resolveEmptyValue();
    },
    resolveSaleDisplayName(row) {
      const rawSaleName = String(row?.sale_name || row?.business_name || row?.project || '').trim();
      const saleCode = String(row?.sale_code || row?.sale?.code || '').trim();

      if (saleCode === '') {
        return rawSaleName;
      }

      if (rawSaleName === '') {
        return saleCode;
      }

      if (rawSaleName.toUpperCase().startsWith(`${saleCode.toUpperCase()} -`)) {
        return rawSaleName;
      }

      return `${saleCode} - ${rawSaleName}`;
    },
    resolveCustomerHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', row?.customer_id);
    },
    resolveSaleHref(row) {
      return resolveRouteHref(this.$router, 'saleDetail', 'saleId', row?.sale_id);
    },
    resolveInvoiceHref(row) {
      return resolveRouteHref(this.$router, 'invoiceDetail', 'invoiceId', row?.id);
    },
    resolvePaymentReceiptId(row) {
      const directId = Number(row?.payment_receipt_id || row?.payment_receipt?.id || 0) || 0;
      if (directId > 0) {
        return directId;
      }

      const paymentItems = Array.isArray(row?.payment)
        ? row.payment
        : (Array.isArray(row?.payments) ? row.payments : []);
      const nestedId = paymentItems
        .map((item) => Number(item?.payment_receipt_id || item?.id || 0) || 0)
        .find((value) => value > 0) || 0;

      return nestedId;
    },
    hasPaymentReceiptRoute(row) {
      return this.resolvePaymentReceiptId(row) > 0;
    },
    resolvePaymentHref(row) {
      return resolveRouteHref(
        this.$router,
        'paymentReceiptDetail',
        'paymentReceiptId',
        this.resolvePaymentReceiptId(row),
      );
    },
    navigateToCustomerDetail(row, event) {
      navigateToRouteDetail(this.$router, 'customerDetail', 'customerId', row?.customer_id, event);
    },
    navigateToSaleDetail(row, event) {
      navigateToRouteDetail(this.$router, 'saleDetail', 'saleId', row?.sale_id, event);
    },
    navigateToInvoiceDetail(row, event) {
      const isModifiedClick = Boolean(
        event
        && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      );
      if (isModifiedClick) {
        return;
      }

      if (event) {
        event.preventDefault();
      }

      this.$emit('view-invoice', row);
    },
    navigateToPaymentDetail(row, event) {
      navigateToRouteDetail(
        this.$router,
        'paymentReceiptDetail',
        'paymentReceiptId',
        this.resolvePaymentReceiptId(row),
        event,
      );
    },
    resolveSerialFolio(row) {
      const serial = String(row?.serial || '').trim();
      const folio = String(row?.folio || '').trim();

      if (serial === '' && folio === '') {
        return this.resolveEmptyValue();
      }

      return `${serial}${folio}`;
    },
    resolveCurrencyCode(row) {
      return String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    shouldShowExchangeRate(row) {
      const currency = this.resolveCurrencyCode(row);
      if (currency === 'MXN') {
        return false;
      }

      const exchangeRate = Number(row?.exchange_rate);
      return Number.isFinite(exchangeRate) && exchangeRate > 0;
    },
    resolveExchangeRateLabel(row) {
      const prefix = String(this.labels?.labels?.exchangeRatePrefix || 'TC:').trim();
      const rate = this.formatExchangeRate(row?.exchange_rate, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
        invalidValue: '',
      });

      if (rate === '') {
        return '';
      }

      return `${prefix} ${rate}`.trim();
    },
    resolveStatusCategory(row) {
      const normalized = String(row?.status_key || row?.status || '').trim().toLowerCase();

      if (['paid', 'done', 'cobrada', 'pagada'].includes(normalized)) {
        return 'paid';
      }

      if (['canceled', 'cancelada'].includes(normalized)) {
        return 'canceled';
      }

      if (['partial', 'parcial', 'partially_paid', 'partially-paid', 'parcialmente pagada'].includes(normalized)) {
        return 'partial';
      }

      if (['pending', 'pendiente'].includes(normalized)) {
        return 'pending';
      }

      return 'pending';
    },
    resolveDisplayStatusKey(row) {
      const normalized = String(row?.status_key || row?.status || '').trim().toLowerCase();

      if (['paid', 'done', 'cobrada', 'pagada'].includes(normalized)) {
        return 'paid';
      }

      if (['canceled', 'cancelada'].includes(normalized)) {
        return 'canceled';
      }

      if (['partial', 'parcial', 'partially_paid', 'partially-paid'].includes(normalized)) {
        return 'partial';
      }

      return 'pending';
    },
    resolveStatusLabel(row) {
      const key = this.resolveDisplayStatusKey(row);
      return this.labels?.statusPills?.[key] || row?.status || this.resolveEmptyValue();
    },
    resolveStatusClass(row) {
      const key = this.resolveDisplayStatusKey(row);
      return {
        'invoices-status-pill--paid': key === 'paid',
        'invoices-status-pill--partial': key === 'partial',
        'invoices-status-pill--pending': key === 'pending',
        'invoices-status-pill--canceled': key === 'canceled',
      };
    },
    hasRelatedPayment(row) {
      const paymentId = Number(row?.payment_id || 0);
      const paymentCode = String(row?.payment?.code || row?.payment_code || '').trim();
      const paymentReference = String(row?.payment_reference || row?.reference || '').trim();

      return paymentId > 0 || paymentCode !== '' || paymentReference !== '';
    },
    showPaymentAction(row) {
      const status = this.resolveStatusCategory(row);
      return status !== 'paid' && status !== 'canceled';
    },
    resolvePaymentReference(row) {
      const collected = [];

      const addValue = (value) => {
        const normalized = String(value || '').trim();
        if (normalized !== '') {
          collected.push(normalized);
        }
      };

      if (Array.isArray(row?.payment)) {
        row.payment.forEach((item) => {
          addValue(item?.code);
        });
      } else {
        addValue(row?.payment?.code);
      }

      if (Array.isArray(row?.payments)) {
        row.payments.forEach((item) => {
          addValue(item?.code);
        });
      }

      if (Array.isArray(row?.payment_receipt_codes)) {
        row.payment_receipt_codes.forEach((item) => addValue(item));
      }

      addValue(row?.payment_receipt_code);
      addValue(row?.payment_code);

      const uniqueValues = [...new Set(collected)];
      return uniqueValues[0] || this.resolveEmptyValue();
    },
    resolveEmptyValue() {
      return String(this.labels?.labels?.emptyValue || '');
    },
    emitAction(actionName, row) {
      this.closeActionsMenu();
      this.$emit(actionName, row);
    },
    toggleActionsMenu(rowId, event) {
      this.openActionsMenuId = resolveToggledMenuId(this.openActionsMenuId, rowId);

      if (this.openActionsMenuId === null) {
        this.closeActionsMenu();
        return;
      }

      const buttonElement = event?.currentTarget || event?.target;
      const position = computeFloatingMenuPosition(buttonElement, 220, 6);
      this.floatingMenuTop = position.top;
      this.floatingMenuLeft = position.left;
    },
    closeActionsMenu() {
      this.openActionsMenuId = null;
    },
    handleGlobalClick(event) {
      if (!this.openActionsMenuId) {
        return;
      }

      const target = event?.target;
      if (!target) {
        return;
      }

      if (target.closest('.app-table-floating-menu') || target.closest('.app-table-actions-menu-wrap')) {
        return;
      }

      this.closeActionsMenu();
    },
  },
};
</script>

<style scoped>
.invoices-table-sort-btn {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
}

.invoices-table-sort-btn--right {
  margin-left: auto;
}

.invoices-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.invoices-cell-stack small {
  font-size: 11px;
  color: #748095;
}

.amount-cell-subtotal,
.amount-cell-total,
.amount-cell-tax-line {
  white-space: nowrap;
}

.amount-cell-exchange-rate {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}

.invoices-table-icon-btn--download {
  color: inherit;
}

.invoices-table-icon-btn--download i {
  color: #b45309;
}

.invoices-table-icon-btn--download:hover,
.invoices-table-icon-btn--download:focus {
  color: inherit;
}

.invoices-table-icon-btn--download:hover i,
.invoices-table-icon-btn--download:focus i {
  color: #92400e;
}

.invoices-table-col-status {
  text-align: center;
}

.invoices-status-pill--paid {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.invoices-status-pill--partial {
  color: #1d4ed8;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.28);
}

.invoices-status-pill--pending {
  color: #b45309;
  background: #fff7e8;
  border: 1px solid rgba(251, 191, 36, 0.55);
}

.invoices-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}
</style>
